import { inject, Injectable } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { PropertyType } from '@shared/enums/property-type.enum';
import { PropertyTypeDetail } from '@shared/enums/property-type-detail.enum';
import { getEnumKeyByValue } from '@shared/utils/enum';
import { EstateTableData } from '@shared/components/estate-table/estate-table.interface';
import { EstateFormEnum } from './estate-form.enum';
import { FILTER_PATTERNS } from '@shared/constants';
import { EstateFormSubmitData } from './estate-form.interface';

@Injectable()
export class EstateFormService {
  private readonly _formBuilder = inject(NonNullableFormBuilder);

  private readonly value = {
    min: 0,
    max: 99999999999999,
  };

  public form = this._formBuilder.group({
    [EstateFormEnum.TYPE_OF_PROPERTY]: this._formBuilder.control<
      keyof typeof PropertyType | ''
    >('', [Validators.required]),
    [EstateFormEnum.PROPERTY_TYPE_DETAIL]: this._formBuilder.control<
      keyof typeof PropertyTypeDetail | ''
    >('', Validators.required),
    [EstateFormEnum.DESCRIPTION]: this._formBuilder.control(''),
    [EstateFormEnum.VALUE]: this._formBuilder.control<string | null>(null, [
      Validators.min(this.value.min),
      Validators.max(this.value.max),
      Validators.pattern(FILTER_PATTERNS.money),
    ]),
  });

  public normalizeFormData(): EstateFormSubmitData {
    const formData = this.form.getRawValue();
    console.log('Form Data:', formData);
    return {
      type: !formData.typeOfProperty ? null : formData.typeOfProperty,
      location_type: !formData.propertyTypeDetail
        ? null
        : formData.propertyTypeDetail,
      description: formData.description,
      value: formData.value || '',
    };
  }

  public refreshFormData(estate: EstateTableData) {
    const typeKey = getEnumKeyByValue(PropertyType, estate.type);
    const locationTypeKey = getEnumKeyByValue(
      PropertyTypeDetail,
      estate.location_type,
    );
    const formattedValue = estate.value.replace(/[^0-9.-]+/g, '');
    this.form.patchValue({
      typeOfProperty: typeKey ?? '',
      propertyTypeDetail: locationTypeKey ?? '',
      description: estate.description,
      value: formattedValue,
    });
    this.handleTypeChange(typeKey);
  }

  public handleTypeChange(value: keyof typeof PropertyType | '' | undefined) {
    const descriptionControl = this.form.controls[EstateFormEnum.DESCRIPTION];
    const typeDetailControl =
      this.form.controls[EstateFormEnum.PROPERTY_TYPE_DETAIL];

    if (value === 'REAL_PROPERTY') {
      typeDetailControl?.addValidators(Validators.required);
      descriptionControl?.markAsTouched();
    } else {
      descriptionControl?.clearValidators();
      typeDetailControl?.clearValidators();
      typeDetailControl?.setValue('');
      typeDetailControl?.markAsUntouched();
    }
    descriptionControl?.updateValueAndValidity();
  }
}
